import React, { useState } from 'react';
import { AlertCircleIcon, ArrowLeftIcon, CalendarIcon, CheckCircleIcon, ClockIcon, DownloadIcon, FileTextIcon, UploadIcon } from 'lucide-react';
export function DocumentDetail({
  document,
  onBack
}) {
  const [newDocument, setNewDocument] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const getStatusIcon = status => {
    switch (status) {
      case 'Active':
        return <CheckCircleIcon className="h-6 w-6 text-green-500" data-id="element-176" />;
      case 'Expiring Soon':
        return <ClockIcon className="h-6 w-6 text-yellow-500" data-id="element-177" />;
      case 'Expired':
        return <AlertCircleIcon className="h-6 w-6 text-red-500" data-id="element-178" />;
      default:
        return <FileTextIcon className="h-6 w-6 text-gray-500" data-id="element-179" />;
    }
  };
  const getStatusColor = status => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Expiring Soon':
        return 'bg-yellow-100 text-yellow-800';
      case 'Expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const formatDate = dateString => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  const getDaysUntilExpiration = expirationDate => {
    if (!expirationDate) return null;
    const today = new Date();
    const expiration = new Date(expirationDate);
    const diffTime = expiration - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  const handleFileChange = e => {
    if (e.target.files.length > 0) {
      setNewDocument(e.target.files[0]);
    }
  };
  const handleUpload = e => {
    e.preventDefault();
    if (!newDocument) return;
    setIsUploading(true);
    // Simulate upload
    setTimeout(() => {
      setIsUploading(false);
      setNewDocument(null);
      // Here you would typically update the document status
      alert('Document uploaded successfully!');
    }, 1500);
  };
  const daysUntilExpiration = getDaysUntilExpiration(document.expirationDate);
  const isExpired = daysUntilExpiration !== null && daysUntilExpiration <= 0;
  const isExpiringSoon = daysUntilExpiration !== null && daysUntilExpiration > 0 && daysUntilExpiration <= 30;
  return <div data-id="element-180">
      <div className="mb-6" data-id="element-181">
        <button onClick={onBack} className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-900" data-id="element-182">
          <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-183" />
          Back to Documents
        </button>
      </div>
      <div className="mb-6 flex justify-between items-start" data-id="element-184">
        <div data-id="element-185">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center" data-id="element-186">
            {document.name}
            <span className={`ml-3 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(document.status)}`} data-id="element-187">
              {document.status}
            </span>
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-188">
            Document ID: {document.id}
          </p>
        </div>
        <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-189">
          <DownloadIcon className="h-4 w-4 mr-2" data-id="element-190" />
          Download
        </button>
      </div>
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6" data-id="element-191">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-192">
          <div data-id="element-193">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-194">
              Document Details
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-195">
              {document.type}
            </p>
          </div>
          <div className="flex items-center" data-id="element-196">
            {getStatusIcon(document.status)}
            <span className="ml-2 text-sm font-medium text-gray-900" data-id="element-197">
              {document.status}
            </span>
          </div>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:px-6" data-id="element-198">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2" data-id="element-199">
            <div className="sm:col-span-1" data-id="element-200">
              <dt className="text-sm font-medium text-gray-500" data-id="element-201">
                Document Type
              </dt>
              <dd className="mt-1 text-sm text-gray-900" data-id="element-202">{document.type}</dd>
            </div>
            <div className="sm:col-span-1" data-id="element-203">
              <dt className="text-sm font-medium text-gray-500" data-id="element-204">Required</dt>
              <dd className="mt-1 text-sm text-gray-900" data-id="element-205">
                {document.required ? 'Yes' : 'No'}
              </dd>
            </div>
            <div className="sm:col-span-1" data-id="element-206">
              <dt className="text-sm font-medium text-gray-500" data-id="element-207">Upload Date</dt>
              <dd className="mt-1 text-sm text-gray-900" data-id="element-208">
                {formatDate(document.uploadedDate)}
              </dd>
            </div>
            <div className="sm:col-span-1" data-id="element-209">
              <dt className="text-sm font-medium text-gray-500" data-id="element-210">
                Expiration Date
              </dt>
              <dd className="mt-1 text-sm text-gray-900 flex items-center" data-id="element-211">
                {document.expirationDate ? <>
                    {formatDate(document.expirationDate)}
                    {isExpired ? <span className="ml-2 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" data-id="element-212">
                        Expired
                      </span> : isExpiringSoon ? <span className="ml-2 px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-213">
                        {daysUntilExpiration} days left
                      </span> : <span className="ml-2 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-214">
                        {daysUntilExpiration} days left
                      </span>}
                  </> : 'No Expiration'}
              </dd>
            </div>
            <div className="sm:col-span-2" data-id="element-215">
              <dt className="text-sm font-medium text-gray-500" data-id="element-216">Description</dt>
              <dd className="mt-1 text-sm text-gray-900" data-id="element-217">
                {document.description}
              </dd>
            </div>
          </dl>
        </div>
      </div>
      {/* Renewal Section */}
      {(isExpired || isExpiringSoon) && <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6" data-id="element-218">
          <div className="px-4 py-5 sm:px-6" data-id="element-219">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-220">
              Document Renewal
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-221">
              {isExpired ? 'This document has expired and needs to be renewed.' : 'This document will expire soon and should be renewed.'}
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:px-6" data-id="element-222">
            <form onSubmit={handleUpload} data-id="element-223">
              <div className="mb-4" data-id="element-224">
                <label className="block text-sm font-medium text-gray-700" data-id="element-225">
                  Upload New Document
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md" data-id="element-226">
                  <div className="space-y-1 text-center" data-id="element-227">
                    <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true" data-id="element-228">
                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" data-id="element-229" />
                    </svg>
                    <div className="flex text-sm text-gray-600" data-id="element-230">
                      <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500" data-id="element-231">
                        <span data-id="element-232">Upload a file</span>
                        <input id="file-upload" name="file-upload" type="file" className="sr-only" onChange={handleFileChange} data-id="element-233" />
                      </label>
                      <p className="pl-1" data-id="element-234">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-235">
                      PDF, PNG, JPG up to 10MB
                    </p>
                  </div>
                </div>
              </div>
              {newDocument && <div className="mb-4 p-3 bg-gray-50 rounded-md" data-id="element-236">
                  <div className="flex items-center justify-between" data-id="element-237">
                    <div className="flex items-center" data-id="element-238">
                      <FileTextIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-239" />
                      <span className="text-sm text-gray-900" data-id="element-240">
                        {newDocument.name}
                      </span>
                    </div>
                    <button type="button" className="text-sm text-red-600 hover:text-red-900" onClick={() => setNewDocument(null)} data-id="element-241">
                      Remove
                    </button>
                  </div>
                </div>}
              <div className="mb-4" data-id="element-242">
                <label htmlFor="expiration-date" className="block text-sm font-medium text-gray-700" data-id="element-243">
                  New Expiration Date
                </label>
                <div className="mt-1 relative rounded-md shadow-sm" data-id="element-244">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-245">
                    <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-246" />
                  </div>
                  <input type="date" name="expiration-date" id="expiration-date" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" min={new Date().toISOString().split('T')[0]} data-id="element-247" />
                </div>
              </div>
              <div className="flex justify-end" data-id="element-248">
                <button type="submit" disabled={!newDocument || isUploading} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50" data-id="element-249">
                  {isUploading ? <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-250">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-251"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-252"></path>
                      </svg>
                      Uploading...
                    </> : <>
                      <UploadIcon className="h-4 w-4 mr-2" data-id="element-253" />
                      Upload Renewal
                    </>}
                </button>
              </div>
            </form>
          </div>
        </div>}
      {/* Document History */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-254">
        <div className="px-4 py-5 sm:px-6" data-id="element-255">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-256">
            Document History
          </h3>
        </div>
        <div className="border-t border-gray-200" data-id="element-257">
          <ul className="divide-y divide-gray-200" data-id="element-258">
            <li className="px-4 py-4 sm:px-6" data-id="element-259">
              <div className="flex items-center justify-between" data-id="element-260">
                <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-261">
                  Document Uploaded
                </p>
                <div className="ml-2 flex-shrink-0 flex" data-id="element-262">
                  <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800" data-id="element-263">
                    {formatDate(document.uploadedDate)}
                  </p>
                </div>
              </div>
              <div className="mt-2 sm:flex sm:justify-between" data-id="element-264">
                <div className="sm:flex" data-id="element-265">
                  <p className="flex items-center text-sm text-gray-500" data-id="element-266">
                    Initial document upload
                  </p>
                </div>
              </div>
            </li>
            {document.status === 'Expiring Soon' && <li className="px-4 py-4 sm:px-6" data-id="element-267">
                <div className="flex items-center justify-between" data-id="element-268">
                  <p className="text-sm font-medium text-yellow-600 truncate" data-id="element-269">
                    Expiration Notification Sent
                  </p>
                  <div className="ml-2 flex-shrink-0 flex" data-id="element-270">
                    <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800" data-id="element-271">
                      {formatDate(new Date(new Date().setDate(new Date().getDate() - 5)))}
                    </p>
                  </div>
                </div>
                <div className="mt-2 sm:flex sm:justify-between" data-id="element-272">
                  <div className="sm:flex" data-id="element-273">
                    <p className="flex items-center text-sm text-gray-500" data-id="element-274">
                      Automatic notification of upcoming expiration
                    </p>
                  </div>
                </div>
              </li>}
            {document.status === 'Expired' && <>
                <li className="px-4 py-4 sm:px-6" data-id="element-275">
                  <div className="flex items-center justify-between" data-id="element-276">
                    <p className="text-sm font-medium text-yellow-600 truncate" data-id="element-277">
                      Expiration Notification Sent
                    </p>
                    <div className="ml-2 flex-shrink-0 flex" data-id="element-278">
                      <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800" data-id="element-279">
                        {formatDate(new Date(new Date(document.expirationDate).setDate(new Date(document.expirationDate).getDate() - 30)))}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 sm:flex sm:justify-between" data-id="element-280">
                    <div className="sm:flex" data-id="element-281">
                      <p className="flex items-center text-sm text-gray-500" data-id="element-282">
                        Automatic notification of upcoming expiration
                      </p>
                    </div>
                  </div>
                </li>
                <li className="px-4 py-4 sm:px-6" data-id="element-283">
                  <div className="flex items-center justify-between" data-id="element-284">
                    <p className="text-sm font-medium text-red-600 truncate" data-id="element-285">
                      Document Expired
                    </p>
                    <div className="ml-2 flex-shrink-0 flex" data-id="element-286">
                      <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800" data-id="element-287">
                        {formatDate(document.expirationDate)}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 sm:flex sm:justify-between" data-id="element-288">
                    <div className="sm:flex" data-id="element-289">
                      <p className="flex items-center text-sm text-gray-500" data-id="element-290">
                        Document has expired and needs renewal
                      </p>
                    </div>
                  </div>
                </li>
              </>}
          </ul>
        </div>
      </div>
    </div>;
}